import type {Dispatch, ThunkAction, GetState} from 'src/reducers';
import type {
  Entity,
  FilterByID,
  MetricsType,
  J2FiltersOptions,
  J2SelectedFilters,
  SelectedWorkflowFilterColumnsData,
  CurrentWorkflowAndNodeBreadCrumbData,
  SavedDashboardFiltersList,
  SavedDashboardFiltersListQuery,
  SavedDashboardFiltersListData,
  SelectedDashboardEntityFilters,
} from 'src/types/analytics-genesis';
import type {ApiOptions, ApiPostOptions} from 'src/utils/redux-api';
import type {LineSeriesItem} from '@spaced-out/ui-design-system/lib/components/Charts';
import type {DateRangeWithTimezone} from '@spaced-out/ui-design-system/lib/types';
import {isEqual, omit} from 'lodash';
import * as reduxApi from 'src/utils/redux-api-v2';
import * as reduxApiBff from 'src/utils/redux-api-bff';
import {thunkify as flow} from 'src/utils/thunks';
import {ANALYTICS_API_V3} from 'src/utils/analytics-api-migration';
import {fetching, key} from 'src/utils/redux';
import {
  workflowTableColumnsQuery,
  nodeTableColumnsQuery,
  peopleWorkflowTableColumnsQuery,
  transformEntities,
  getWorkflowDashboardName,
} from 'src/utils/analytics-genesis';


export const RECEIVE_FILTERS_OPTIONS =
  'analytics-workflows-overview/receive-filters-options';
export const RECEIVE_WORKFLOW_OPTIONS =
  'analytics-workflows-overview/receive-workflow-options';
export const RECEIVE_WORKFLOW_PLOT_DATA =
  'analytics-workflows-overview/receive-workflow-plot-data';
export const RECEIVE_SAVED_DASHBOARDS =
  'analytics-workflows-overview/receive-saved-dashboards';
export const SET_CHART_FREQUENCY =
  'analytics-workflows-overview/set-chart-frequency';
export const SET_SELECTED_FILTERS =
  'analytics-workflows-overview/set-selected-filters';
export const SET_SELECTED_ENTITIES =
  'analytics-workflows-overview/set-selected-entities';
export const SET_SELECTED_WORKFLOWS =
  'analytics-workflows-overview/set-selected-workflows';
export const SET_SELECTED_METRICS_TYPE =
  'analytics-workflows-overview/set-selected-metrics-type';
export const SET_SELECTED_DATE_RANGE_AND_FREQUENCY =
  'analytics-workflows-overview/set-selected-date-range-and-frequency';
export const CLEAR_SELECTED_FILTERS =
  'analytics-workflows-overview/clear-filters';

export const RECEIVE_WORKFLOW_TABLE_COLUMNS =
  'analytics-workflows-overview/receive-workflow-table-columns';
export const RECEIVE_NODE_TABLE_COLUMNS =
  'analytics-workflows-overview/receive-node-table-columns';
export const RECEIVE_PEOPLE_TABLE_COLUMNS =
  'analytics-workflows-overview/receive-people-table-columns';

export const SET_CURRENT_WORKFLOW_AND_NODE_DATA =
  'analytics-workflows-overview/set-current-workflow-and-node-data';
export const SET_CURRENT_DASHBOARD =
  'analytics-workflows-overview/set-current-dashboard';
export const SET_LOADING_DASHBOARD_FILTERS =
  'analytics-workflows-overview/loading-dashboard-filters';

export const SET_SELECTED_DASHBOARD_ENTITY_FILTERS =
  'analytics-workflows-overview/set-selected-dashboard-entity-filters';

export const SET_NAVIGATION_DOWN =
  'analytics-workflows-overview/set-navigation-down';

export const SET_NAVIGATION_UP =
  'analytics-workflows-overview/set-navigation-up';

export const SET_ALL_WORKFLOW_ENTITIES =
  'analytics-workflows-overview/set-all-workflow-entities';
export const SET_ALL_NODE_ENTITIES =
  'analytics-workflows-overview/set-all-node-entities';
export const SET_ALL_PEOPLE_ENTITIES =
  'analytics-workflows-overview/set-all-people-entities';

export const SET_ALL_WORKFLOW_FILTERS =
  'analytics-workflows-overview/set-all-workflow-filters';
export const SET_ALL_NODE_FILTERS =
  'analytics-workflows-overview/set-all-node-filters';
export const SET_ALL_PEOPLE_FILTERS =
  'analytics-workflows-overview/set-all-people-filters';

export const SET_SELECTED_DATES =
  'analytics-workflows-overview/set-selected-dates';

type ReceiveWorkflowPlotDataAction = {
  type: typeof RECEIVE_WORKFLOW_PLOT_DATA;
  payload: Array<LineSeriesItem>;
};

type SetChartFrequencyAction = {
  type: typeof SET_CHART_FREQUENCY;
  payload: string;
};

type SetSelectedFiltersAction = {
  type: typeof SET_SELECTED_FILTERS;
  payload: {
    dashboardKey: string;
    filters: J2SelectedFilters;
  };
};

type SetSelectedEntitiesAction = {
  type: typeof SET_SELECTED_ENTITIES;
  payload: {
    dashboardKey: string;
    entities: Array<Entity>;
  };
};

type SetSelectedWorkflowsAction = {
  type: typeof SET_SELECTED_WORKFLOWS;
  payload: {
    dashboardKey: string;
    workflows: Array<FilterByID>;
  };
};

type SetSelectedMetricsTypeAction = {
  type: typeof SET_SELECTED_METRICS_TYPE;
  payload: MetricsType;
};

type SetSelectedDateRangeAndFrequencyAction = {
  type: typeof SET_SELECTED_DATE_RANGE_AND_FREQUENCY;
  payload: DateRangeWithTimezone & {
    chartFrequency: string;
    dashboardKey: string;
  };
};

type ReceiveFiltersOptionsAction = {
  type: typeof RECEIVE_FILTERS_OPTIONS;
  payload: J2FiltersOptions;
};

type ReceiveWorkflowOptionsAction = {
  type: typeof RECEIVE_WORKFLOW_OPTIONS;
  payload: Array<FilterByID>;
};

type ClearSelectedFiltersAction = {
  type: typeof CLEAR_SELECTED_FILTERS;
  payload: {
    dashboardKey: string;
  };
};

type ReceiveWorkflowTableColumnsAction = {
  type: typeof RECEIVE_WORKFLOW_TABLE_COLUMNS;
  payload: SelectedWorkflowFilterColumnsData;
};

type ReceiveNodeTableColumnsAction = {
  type: typeof RECEIVE_NODE_TABLE_COLUMNS;
  payload: SelectedWorkflowFilterColumnsData;
};

type ReceivePeopleTableColumnsAction = {
  type: typeof RECEIVE_PEOPLE_TABLE_COLUMNS;
  payload: SelectedWorkflowFilterColumnsData;
};

type ReceiveSavedDashboardFilterListAction = {
  type: typeof RECEIVE_SAVED_DASHBOARDS;
  payload: {
    filters: SavedDashboardFiltersListData;
    dashboard: string;
    product: string;
  };
};

type SetCurrentWorkflowAndNodeDataAction = {
  type: typeof SET_CURRENT_WORKFLOW_AND_NODE_DATA;
  payload: CurrentWorkflowAndNodeBreadCrumbData;
};

type SetCurrentDashboardAction = {
  type: typeof SET_CURRENT_DASHBOARD;
  payload: {
    currentDashboard: SavedDashboardFiltersList | null;
    dashboardKey: string;
  };
};

type SetLoadingDashboardFiltersAction = {
  type: typeof SET_LOADING_DASHBOARD_FILTERS;
  payload: boolean;
};

type SetSelectedDashboardEntityFiltersAction = {
  type: typeof SET_SELECTED_DASHBOARD_ENTITY_FILTERS;
  payload: {
    dashboardKey: string;
  } & SelectedDashboardEntityFilters;
};

type SetNavigationDownAction = {
  type: typeof SET_NAVIGATION_DOWN;
  payload: boolean;
};

type SetNavigationUpAction = {
  type: typeof SET_NAVIGATION_UP;
  payload: boolean;
};

type SetAllWorkflowEntitiesAction = {
  type: typeof SET_ALL_WORKFLOW_ENTITIES;
  payload: {
    dashboardKey: string;
    entities: Array<Entity>;
  };
};

type SetAllNodeEntitiesAction = {
  type: typeof SET_ALL_NODE_ENTITIES;
  payload: {
    dashboardKey: string;
    entities: Array<Entity>;
  };
};

type SetAllPeopleEntitiesAction = {
  type: typeof SET_ALL_PEOPLE_ENTITIES;
  payload: {
    dashboardKey: string;
    entities: Array<Entity>;
  };
};

type SetAllWorkflowFiltersAction = {
  type: typeof SET_ALL_WORKFLOW_FILTERS;
  payload: {
    dashboardKey: string;
    filters: J2SelectedFilters;
  };
};

type SetAllNodeFiltersAction = {
  type: typeof SET_ALL_NODE_FILTERS;
  payload: {
    dashboardKey: string;
    filters: J2SelectedFilters;
  };
};

type SetAllPeopleFiltersAction = {
  type: typeof SET_ALL_PEOPLE_FILTERS;
  payload: {
    dashboardKey: string;
    filters: J2SelectedFilters;
  };
};

type SetSelectedDatesAction = {
  type: typeof SET_SELECTED_DATES;
  payload: {
    dashboardKey: string;
    dates: DateRangeWithTimezone;
  };
};

export type AnalyticsWorkflowsOverviewActions =
  | SetChartFrequencyAction
  | SetSelectedFiltersAction
  | SetSelectedEntitiesAction
  | SetSelectedWorkflowsAction
  | SetSelectedMetricsTypeAction
  | SetSelectedDateRangeAndFrequencyAction
  | ReceiveWorkflowPlotDataAction
  | ReceiveWorkflowOptionsAction
  | ReceiveFiltersOptionsAction
  | ClearSelectedFiltersAction
  | ReceiveWorkflowTableColumnsAction
  | ReceiveNodeTableColumnsAction
  | ReceivePeopleTableColumnsAction
  | SetCurrentWorkflowAndNodeDataAction
  | ReceiveSavedDashboardFilterListAction
  | SetCurrentDashboardAction
  | SetLoadingDashboardFiltersAction
  | SetSelectedDashboardEntityFiltersAction
  | SetNavigationDownAction
  | SetAllWorkflowEntitiesAction
  | SetAllNodeEntitiesAction
  | SetAllPeopleEntitiesAction
  | SetAllWorkflowFiltersAction
  | SetAllNodeFiltersAction
  | SetAllPeopleFiltersAction
  | SetSelectedDatesAction
  | SetNavigationUpAction;

export const setChartFrequency = (
  payload: string,
): SetChartFrequencyAction => ({
  type: SET_CHART_FREQUENCY,
  payload,
});

export const setSelectedFilters = (
  dashboardKey: string,
  filters: J2SelectedFilters,
): SetSelectedFiltersAction => ({
  type: SET_SELECTED_FILTERS,
  payload: {dashboardKey, filters},
});

export const setSelectedEntities = (
  dashboardKey: string,
  entities: Array<Entity>,
): SetSelectedEntitiesAction => ({
  type: SET_SELECTED_ENTITIES,
  payload: {dashboardKey, entities},
});

export const setSelectedWorkflows = (
  dashboardKey: string,
  workflows: Array<FilterByID>,
): SetSelectedWorkflowsAction => ({
  type: SET_SELECTED_WORKFLOWS,
  payload: {dashboardKey, workflows},
});

export const setSelectedMetricsType = (
  payload: MetricsType,
): SetSelectedMetricsTypeAction => ({
  type: SET_SELECTED_METRICS_TYPE,
  payload,
});

export const setSelectedDateRangeAndFrequency = (
  payload: DateRangeWithTimezone & {
    chartFrequency: string;
    dashboardKey: string;
  },
): SetSelectedDateRangeAndFrequencyAction => ({
  type: SET_SELECTED_DATE_RANGE_AND_FREQUENCY,
  payload,
});

const receiveWorkflowPlotData = (
  payload: Array<LineSeriesItem>,
): ReceiveWorkflowPlotDataAction => ({
  type: RECEIVE_WORKFLOW_PLOT_DATA,
  payload,
});

const receiveFiltersOptions = (
  payload: J2FiltersOptions,
): ReceiveFiltersOptionsAction => ({
  type: RECEIVE_FILTERS_OPTIONS,
  payload,
});

const receiveWorkflowOptions = (
  payload: Array<FilterByID>,
): ReceiveWorkflowOptionsAction => ({
  type: RECEIVE_WORKFLOW_OPTIONS,
  payload,
});

const receiveWorkflowTableColumnSelection = (
  payload: SelectedWorkflowFilterColumnsData,
): ReceiveWorkflowTableColumnsAction => ({
  type: RECEIVE_WORKFLOW_TABLE_COLUMNS,
  payload,
});

const receiveNodeTableColumnSelection = (
  payload: SelectedWorkflowFilterColumnsData,
): ReceiveNodeTableColumnsAction => ({
  type: RECEIVE_NODE_TABLE_COLUMNS,
  payload,
});

const receivePeopleTableColumnSelection = (
  payload: SelectedWorkflowFilterColumnsData,
): ReceivePeopleTableColumnsAction => ({
  type: RECEIVE_PEOPLE_TABLE_COLUMNS,
  payload,
});

const receiveSavedDashboardFilterListData = (payload: {
  filters: SavedDashboardFiltersListData;
  dashboard: string;
  product: string;
}): ReceiveSavedDashboardFilterListAction => ({
  type: RECEIVE_SAVED_DASHBOARDS,
  payload,
});

export const setCurrentWorkflowAndNodeData = (
  payload: CurrentWorkflowAndNodeBreadCrumbData,
): SetCurrentWorkflowAndNodeDataAction => ({
  type: SET_CURRENT_WORKFLOW_AND_NODE_DATA,
  payload,
});

export const clearSelectedFilters = (
  dashboardKey: string,
): ClearSelectedFiltersAction => ({
  type: CLEAR_SELECTED_FILTERS,
  payload: {dashboardKey},
});

export const setCurrentDashboard = (
  dashboardKey: string,
  currentDashboard: SavedDashboardFiltersList | null,
): SetCurrentDashboardAction => ({
  type: SET_CURRENT_DASHBOARD,
  payload: {dashboardKey, currentDashboard},
});

export const setLoadingDashboardFilters = (
  payload: boolean,
): SetLoadingDashboardFiltersAction => ({
  type: SET_LOADING_DASHBOARD_FILTERS,
  payload,
});

export const setSelectedDashboardEntityFilters = (
  dashboardKey: string,
  entities: Array<Entity>,
): SetSelectedDashboardEntityFiltersAction => ({
  type: SET_SELECTED_DASHBOARD_ENTITY_FILTERS,
  payload: {dashboardKey, entities},
});

export const setNavigationDown = (
  isNavigationDown: boolean,
): SetNavigationDownAction => ({
  type: SET_NAVIGATION_DOWN,
  payload: isNavigationDown,
});

export const setNavigationUp = (
  isNavigationUp: boolean,
): SetNavigationUpAction => ({
  type: SET_NAVIGATION_UP,
  payload: isNavigationUp,
});

export const setAllWorkflowEntities = (
  dashboardKey: string,
  entities: Array<Entity>,
): SetAllWorkflowEntitiesAction => ({
  type: SET_ALL_WORKFLOW_ENTITIES,
  payload: {dashboardKey, entities},
});

export const setAllNodeEntities = (
  dashboardKey: string,
  entities: Array<Entity>,
): SetAllNodeEntitiesAction => ({
  type: SET_ALL_NODE_ENTITIES,
  payload: {dashboardKey, entities},
});

export const setAllPeopleEntities = (
  dashboardKey: string,
  entities: Array<Entity>,
): SetAllPeopleEntitiesAction => ({
  type: SET_ALL_PEOPLE_ENTITIES,
  payload: {dashboardKey, entities},
});

export const setAllWorkflowFilters = (
  dashboardKey: string,
  filters: J2SelectedFilters,
): SetAllWorkflowFiltersAction => ({
  type: SET_ALL_WORKFLOW_FILTERS,
  payload: {dashboardKey, filters},
});

export const setAllNodeFilters = (
  dashboardKey: string,
  filters: J2SelectedFilters,
): SetAllNodeFiltersAction => ({
  type: SET_ALL_NODE_FILTERS,
  payload: {dashboardKey, filters},
});

export const setAllPeopleFilters = (
  dashboardKey: string,
  filters: J2SelectedFilters,
): SetAllPeopleFiltersAction => ({
  type: SET_ALL_PEOPLE_FILTERS,
  payload: {dashboardKey, filters},
});

export const setSelectedDates = (
  dashboardKey: string,
  dates: DateRangeWithTimezone,
): SetSelectedDatesAction => ({
  type: SET_SELECTED_DATES,
  payload: {dashboardKey, dates},
});

const analyticsOptions: ApiOptions = {apiPath: ANALYTICS_API_V3};
const analyticsApiOptions: ApiPostOptions = {apiPath: ANALYTICS_API_V3};

export const getFiltersOptions: () => ThunkAction<unknown> = flow(
  key(() => `filtersOptions`),
  fetching(),
)(() => async (dispatch: Dispatch) => {
  const allFilters = {
    filter_list: [
      'node_type',
      'workflow_name',
      'recipient_type',
      'workflow_status',
      'workflow_category',
    ],
  } as const;
  try {
    const response = await dispatch(
      reduxApi.post('/filters', allFilters, {}, analyticsApiOptions),
    );
    dispatch(receiveFiltersOptions(response));
  } catch (error: any) {
    dispatch(
      receiveFiltersOptions({
        node_type: [],
        workflow_name: [],
        recipient_type: [],
        workflow_status: [],
        workflow_category: [],
      }),
    );
    console.error('Error Fetching Filters Options:', error);
  }
});

export const getWorkflowOptions: (
  query: string,
  limit: number,
) => ThunkAction<unknown> = flow(
  key((query, limit) => `workflowOptions-${query}-${limit}`),
  fetching(),
)((query, limit) => async (dispatch: Dispatch) => {
  const workflowFilter = {
    filter_list: ['workflow_name'],
    query,
    limit,
  } as const;
  try {
    const {workflow_name} = await dispatch(
      reduxApi.post('/filters', workflowFilter, {}, analyticsApiOptions),
    );
    dispatch(receiveWorkflowOptions(workflow_name));
  } catch (error: any) {
    dispatch(receiveWorkflowOptions([]));
    console.error('Error Fetching Workflow Options:', error);
  }
});

export const getWorkflowPlotData: (
  data: Record<any, any>,
) => ThunkAction<unknown> = flow(
  key(() => `workflowPlotData`),
  fetching(),
)((data) => async (dispatch: Dispatch) => {
  try {
    const workflowPlotData = await dispatch(
      reduxApi.post(`/workflows-overview/plot`, data, {}, analyticsApiOptions),
    );
    dispatch(receiveWorkflowPlotData(workflowPlotData.series));
  } catch (error: any) {
    dispatch(receiveWorkflowPlotData([]));
    console.error('Error Fetching Plot Data:', error);
  }
});

export const getWorkflowTableFilterColumns: () => ThunkAction<unknown> = flow(
  key(() => 'getWorkflowTableFilterColumns'),
  fetching(),
)(() => async (dispatch: Dispatch) => {
  try {
    const payload: SelectedWorkflowFilterColumnsData = await dispatch(
      reduxApi.get(
        '/workflows-overview/column-selection',
        workflowTableColumnsQuery,
        analyticsOptions,
      ),
    );
    dispatch(receiveWorkflowTableColumnSelection(payload));
  } catch (error: any) {
    console.error('Error fetching workflow table column filter:', error);
  }
});

export const getNodeTableFilterColumns: () => ThunkAction<unknown> = flow(
  key(() => 'getNodeTableFilterColumns'),
  fetching(),
)(() => async (dispatch: Dispatch) => {
  try {
    const payload: SelectedWorkflowFilterColumnsData = await dispatch(
      reduxApi.get(
        '/workflows-overview/column-selection',
        nodeTableColumnsQuery,
        analyticsOptions,
      ),
    );
    dispatch(receiveNodeTableColumnSelection(payload));
  } catch (error: any) {
    console.error('Error fetching node table column filter:', error);
  }
});

export const getPeopleTableFilterColumns: () => ThunkAction<unknown> = flow(
  key(() => 'getPeopleTableFilterColumns'),
  fetching(),
)(() => async (dispatch: Dispatch) => {
  try {
    const payload: SelectedWorkflowFilterColumnsData = await dispatch(
      reduxApi.get(
        '/workflows-overview/column-selection',
        peopleWorkflowTableColumnsQuery,
        analyticsOptions,
      ),
    );
    dispatch(receivePeopleTableColumnSelection(payload));
  } catch (error: any) {
    console.error('Error fetching node table column filter:', error);
  }
});

export const saveWorkflowSelectedFilterColumns: (
  columnData: SelectedWorkflowFilterColumnsData,
) => ThunkAction<unknown> = flow(
  key(
    (columnData) =>
      `saveWorkflowSelectedFiltersColumns-${JSON.stringify(columnData)}`,
  ),
  fetching(),
)(
  (columnData) => async (dispatch: Dispatch) =>
    dispatch(
      reduxApi.post(
        '/workflows-overview/column-selection',
        columnData,
        {},
        analyticsApiOptions,
      ),
    ),
);

export const getWorkflowNodeDetails: (
  workflowId: string,
  nodeId?: string,
) => ThunkAction<unknown> = flow(
  key((workflowId) => `getWorkflowNodeDetails-${workflowId}`),
  fetching(),
)((workflowId, nodeId) => async (dispatch: Dispatch) => {
  try {
    const currentWorkflowAndNode: CurrentWorkflowAndNodeBreadCrumbData = {
      workflow: {},
      node: {},
    };
    if (workflowId) {
      const workflowResponse = await dispatch(
        reduxApiBff.get(`workflow/${workflowId}`),
      );
      currentWorkflowAndNode.workflow = {
        id: workflowId,
        name: workflowResponse?.name,
      };

      if (nodeId) {
        const {node_name, body} = await dispatch(
          reduxApiBff.get('node', {
            node_id: nodeId,
          }),
        );

        let sendType = body.type.toLowerCase();

        if (sendType.includes('email')) {
          sendType = 'email';
        }
        if (sendType.includes('sms')) {
          sendType = 'sms';
        }

        currentWorkflowAndNode.node = {
          id: nodeId,
          name: `${node_name}`,
          sendType,
        };
      } else {
        currentWorkflowAndNode.node = {};
      }
    }
    dispatch(setCurrentWorkflowAndNodeData(currentWorkflowAndNode));
  } catch (error: any) {
    console.error('Error saving overview column filter:', error);
  }
});

export const getSavedAllWorkflowDashboards: (
  query: SavedDashboardFiltersListQuery,
) => ThunkAction<unknown> = flow(
  key(() => 'j2-analytics-deliverability-engagement-saved-dashboards'),
  fetching(),
)((query) => async (dispatch: Dispatch, getState: GetState) => {
  try {
    dispatch(setLoadingDashboardFilters(true));
    const {
      filters,
    }: {
      filters: SavedDashboardFiltersListData;
    } = await dispatch(
      reduxApi.get('/workflows-overview/save-filter', query, analyticsOptions),
    );
    dispatch(receiveSavedDashboardFilterListData({filters, ...query}));
    const loadSavedFilters = () => {
      const defaultDashboard = filters.find(({is_default}) => is_default);
      if (defaultDashboard) {
        const {filters = {}} = defaultDashboard;
        const {
          entity_data_filter = [{id: new Date().getTime()}],
          workflow_categories = [],
          workflow_status = [],
          recipient_type = [],
          workflow_ids = [],
          node_types = [],
          selected_date_range = {
            startDateUTC: null,
            startDate: null,
            endDateUTC: null,
            endDate: null,
          },
        } = filters ?? {};
        dispatch(setSelectedEntities('workflows_all', entity_data_filter));
        dispatch(
          setSelectedFilters('workflows_all', {
            workflow_categories,
            workflow_status,
            recipient_type,
            workflow_ids: workflow_ids.map(({id}) => id),
            node_types,
          }),
        );
        dispatch(setSelectedDates('workflows_all', selected_date_range));
      }
      dispatch(setCurrentDashboard('workflows_all', defaultDashboard ?? null));
    };
    loadSavedFilters();
  } catch (error: any) {
    console.error('Error fetching saved dashboard filter:', error);
  } finally {
    dispatch(setLoadingDashboardFilters(false));
    dispatch(setNavigationDown(false));
    dispatch(setNavigationUp(false));
  }
});

export const getSavedAllNodeDashboards: (
  query: SavedDashboardFiltersListQuery,
  workflowId: string,
) => ThunkAction<unknown> = flow(
  key(() => 'j2-analytics-deliverability-engagement-saved-dashboards'),
  fetching(),
)((query, workflowId) => async (dispatch: Dispatch, getState: GetState) => {
  try {
    dispatch(setLoadingDashboardFilters(true));
    const {
      filters,
    }: {
      filters: SavedDashboardFiltersListData;
    } = await dispatch(
      reduxApi.get('/workflows-overview/save-filter', query, analyticsOptions),
    );
    dispatch(receiveSavedDashboardFilterListData({filters, ...query}));

    const dashboardName = getWorkflowDashboardName(workflowId, undefined);
    const isNavigationDown =
      getState().analyticsWorkflowsOverview.isNavigationDown;

    const isNavigationUp = getState().analyticsWorkflowsOverview.isNavigationUp;

    const parentEntitiesFilters = getState().analyticsWorkflowsOverview
      .selectedEntities['workflows_all'] ?? [{id: new Date().getTime()}];

    const parentWorkflowFilters =
      getState().analyticsWorkflowsOverview.selectedFilters['workflows_all'] ??
      {};

    const parentDateRange = getState().analyticsWorkflowsOverview.selectedDates[
      'workflows_all'
    ] ?? {
      startDateUTC: null,
      startDate: null,
      endDateUTC: null,
      endDate: null,
    };

    const savedDashboardFilters =
      getState().analyticsWorkflowsOverview.saveDashboardFilterList[
        'workflows_all'
      ] ?? [];

    const parentDefaultDashboard = savedDashboardFilters?.find(
      ({is_default}) => is_default,
    );

    const {filters: parentFilters = {}} = parentDefaultDashboard ?? {};

    const {
      entity_data_filter: entityFilters = [{id: new Date().getTime()}],
      selected_date_range = {
        startDateUTC: null,
        startDate: null,
        endDateUTC: null,
        endDate: null,
      },
      ...workflowFilters
    } = parentFilters ?? {};

    const loadSavedFilters = () => {
      const defaultDashboard = filters.find(({is_default}) => is_default);
      if (defaultDashboard) {
        const {filters = {}} = defaultDashboard;
        const {
          entity_data_filter = [{id: new Date().getTime()}],
          workflow_categories = [],
          workflow_status = [],
          recipient_type = [],
          workflow_ids = [],
          node_types = [],
          selected_date_range = {
            startDateUTC: null,
            startDate: null,
            endDateUTC: null,
            endDate: null,
          },
        } = filters ?? {};
        dispatch(setSelectedEntities(dashboardName, entity_data_filter));
        dispatch(
          setSelectedFilters(dashboardName, {
            workflow_categories,
            workflow_status,
            recipient_type,
            workflow_ids: workflow_ids.map(({id}) => id),
            node_types,
          }),
        );
        dispatch(setSelectedDates(dashboardName, selected_date_range));
      }
      dispatch(setCurrentDashboard('workflows_all', defaultDashboard ?? null));
    };

    if (isNavigationDown === null || isNavigationUp) {
      loadSavedFilters();
      return;
    }

    if (isNavigationDown) {
      const isEntityFiltersChanged = !isEqual(
        transformEntities(entityFilters),
        transformEntities(parentEntitiesFilters),
      );

      const isWorkflowFiltersChanged = !isEqual(
        workflowFilters,
        parentWorkflowFilters,
      );

      const isDateRangeChanged = !isEqual(parentDateRange, selected_date_range);

      if (
        isEntityFiltersChanged ||
        isWorkflowFiltersChanged ||
        isDateRangeChanged
      ) {
        dispatch(setSelectedEntities(dashboardName, parentEntitiesFilters));
        dispatch(setSelectedFilters(dashboardName, parentWorkflowFilters));
        dispatch(setSelectedDates(dashboardName, parentDateRange));
      } else {
        loadSavedFilters();
      }
      return;
    }
  } catch (error: any) {
    console.error('Error fetching saved dashboard filter:', error);
  } finally {
    dispatch(setLoadingDashboardFilters(false));
    dispatch(setNavigationDown(false));
    dispatch(setNavigationUp(false));
  }
});

export const getSavedAllPeopleDashboards: (
  query: SavedDashboardFiltersListQuery,
  workflowId: string,
  nodeId: string,
) => ThunkAction<unknown> = flow(
  key(() => 'j2-analytics-deliverability-engagement-saved-dashboards'),
  fetching(),
)(
  (query, workflowId, nodeId) =>
    async (dispatch: Dispatch, getState: GetState) => {
      try {
        dispatch(setLoadingDashboardFilters(true));
        const {
          filters,
        }: {
          filters: SavedDashboardFiltersListData;
        } = await dispatch(
          reduxApi.get(
            '/workflows-overview/save-filter',
            query,
            analyticsOptions,
          ),
        );
        dispatch(receiveSavedDashboardFilterListData({filters, ...query}));
        const dashboardName = getWorkflowDashboardName(workflowId, nodeId);
        const isNavigationDown =
          getState().analyticsWorkflowsOverview.isNavigationDown;

        const parentEntitiesFilters = getState().analyticsWorkflowsOverview
          .selectedEntities[`workflows_${workflowId}`] ?? [
          {id: new Date().getTime()},
        ];

        const parentWorkflowFilters =
          getState().analyticsWorkflowsOverview.selectedFilters[
            `workflows_${workflowId}`
          ] ?? {};

        const parentDateRange = getState().analyticsWorkflowsOverview
          .selectedDates[`workflows_${workflowId}`] ?? {
          startDateUTC: null,
          startDate: null,
          endDateUTC: null,
          endDate: null,
        };

        const savedDashboardFilters =
          getState().analyticsWorkflowsOverview.saveDashboardFilterList[
            `workflows_${workflowId}`
          ] ?? [];

        const parentDefaultDashboard = savedDashboardFilters?.find(
          ({is_default}) => is_default,
        );

        const {filters: parentFilters = {}} = parentDefaultDashboard ?? {};
        const {
          entity_data_filter: entityFilters = [{id: new Date().getTime()}],
          selected_date_range = {
            startDateUTC: null,
            startDate: null,
            endDateUTC: null,
            endDate: null,
          },
          ...workflowFilters
        } = parentFilters ?? {};

        const loadSavedFilters = () => {
          const defaultDashboard = filters.find(({is_default}) => is_default);
          if (defaultDashboard) {
            const {filters = {}} = defaultDashboard;
            const {
              entity_data_filter = [{id: new Date().getTime()}],
              workflow_categories = [],
              workflow_status = [],
              recipient_type = [],
              workflow_ids = [],
              node_types = [],
            } = filters ?? {};
            dispatch(setSelectedEntities(dashboardName, entity_data_filter));
            dispatch(
              setSelectedFilters(dashboardName, {
                workflow_categories,
                workflow_status,
                recipient_type,
                workflow_ids: workflow_ids.map(({id}) => id),
                node_types,
              }),
            );
            dispatch(setSelectedDates(dashboardName, selected_date_range));
          }
          dispatch(
            setCurrentDashboard('workflows_all', defaultDashboard ?? null),
          );
        };

        if (isNavigationDown === null) {
          loadSavedFilters();
          return;
        }

        if (isNavigationDown) {
          const isEntityFiltersChanged = !isEqual(
            transformEntities(entityFilters),
            transformEntities(parentEntitiesFilters),
          );

          const isWorkflowFiltersChanged = !isEqual(
            workflowFilters,
            parentWorkflowFilters,
          );

          const isDateRangeChanged = !isEqual(
            parentDateRange,
            selected_date_range,
          );

          if (
            isEntityFiltersChanged ||
            isWorkflowFiltersChanged ||
            isDateRangeChanged
          ) {
            dispatch(setSelectedEntities(dashboardName, parentEntitiesFilters));
            dispatch(setSelectedFilters(dashboardName, parentWorkflowFilters));
            dispatch(setSelectedDates(dashboardName, parentDateRange));
          } else {
            loadSavedFilters();
          }
          return;
        }
      } catch (error: any) {
        console.error('Error fetching saved dashboard filter:', error);
      } finally {
        dispatch(setLoadingDashboardFilters(false));
        dispatch(setNavigationDown(false));
        dispatch(setNavigationUp(false));
      }
    },
);

export const saveWorkflowDashboard: (
  dashboardData: SavedDashboardFiltersList,
  dashboardKey: string,
) => ThunkAction<unknown> = flow(
  key(
    (dashboardData) => `saveWorkflowDashboard-${JSON.stringify(dashboardData)}`,
  ),
  fetching(),
)(
  (dashboardData, dashboardKey) =>
    async (dispatch: Dispatch, getState: GetState) => {
      try {
        const {
          filters,
        }: {
          filters: SavedDashboardFiltersListData;
        } = await dispatch(
          reduxApi.post(
            '/workflows-overview/save-filter',
            dashboardData,
            {},
            analyticsApiOptions,
          ),
        );
        const {product, dashboard, id: dashboardId} = dashboardData;
        dispatch(
          receiveSavedDashboardFilterListData({filters, product, dashboard}),
        );

        let newDashboard = null;

        if (!dashboardId) {
          newDashboard = filters.find(({name}) => name === dashboardData.name);
        }

        return newDashboard ?? null;
      } catch (error: any) {
        console.error('Error fetching saved dashboard filter:', error);
        throw error;
      }
    },
);
